import type { CSSProperties } from 'react';

export type FooterThemeKey = 'light' | 'dark';
export type FooterLayoutKey = 'md' | 'lg' | 'xl' | 'full';

export type FooterThemeStyles = {
    top: CSSProperties;
    bottom: CSSProperties;
    titleColor: string;
    linkColor: string;
    linkHoverColor: string;
    socialItemBackground: string;
    socialTextColor: string;
    socialHoverColor: string;
    iconColor: string;
    logoFilter?: string;
};

export type FooterLayoutStyles = {
    container: CSSProperties;
    topPadding: CSSProperties;
    bottomPadding: CSSProperties;
    desktopMaxColumns: number;
};

export type FooterStyles = {
    footer: CSSProperties;
    topInner: CSSProperties;
    columnsGrid: CSSProperties;
    column: CSSProperties;
    title: CSSProperties;
    link: CSSProperties;
    brandBlock: CSSProperties;
    logo: CSSProperties;
    socialWrap: CSSProperties;
    bottomInner: CSSProperties;
    bottomText: CSSProperties;
};

export const footerThemeTokens: Record<FooterThemeKey, FooterThemeStyles> = {
    light: {
        top: {
            background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
            color: '#111827',
        },
        bottom: {
            background: '#e5e7eb',
            color: '#111827',
        },
        titleColor: '#1f2937',
        linkColor: '#374151',
        linkHoverColor: '#111827',
        socialItemBackground: 'rgba(17, 24, 39, 0.06)',
        socialTextColor: '#374151',
        socialHoverColor: '#111827',
        iconColor: '#374151',
    },
    dark: {
        top: {
            background: 'linear-gradient(to bottom, #2b2b2b 0%, #0b0c1a 100%)',
            color: '#ffffff',
        },
        bottom: {
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'rgba(255, 255, 255, 0.78)',
        },
        titleColor: 'rgba(255, 255, 255, 0.86)',
        linkColor: 'rgba(255, 255, 255, 0.74)',
        linkHoverColor: '#ffffff',
        socialItemBackground: 'rgba(255, 255, 255, 0.08)',
        socialTextColor: '#e5e7eb',
        socialHoverColor: '#ffffff',
        iconColor: '#efefef',
    },
};

export const footerLayoutTokens: Record<FooterLayoutKey, FooterLayoutStyles> = {
    md: {
        container: {
            maxWidth: '896px',
            margin: '0 auto',
            width: '100%',
        },
        topPadding: {
            padding: '32px 24px',
        },
        bottomPadding: {
            padding: '8px 24px',
        },
        desktopMaxColumns: 4,
    },
    lg: {
        container: {
            maxWidth: '1152px',
            margin: '0 auto',
            width: '100%',
        },
        topPadding: {
            padding: '40px 32px',
        },
        bottomPadding: {
            padding: '8px 32px',
        },
        desktopMaxColumns: 4,
    },
    xl: {
        container: {
            maxWidth: '1280px',
            margin: '0 auto',
            width: '100%',
        },
        topPadding: {
            padding: '48px 40px',
        },
        bottomPadding: {
            padding: '8px 40px',
        },
        desktopMaxColumns: 4,
    },
    full: {
        container: {
            maxWidth: '100%',
            margin: '0 auto',
            width: '100%',
        },
        topPadding: {
            padding: '40px 24px',
        },
        bottomPadding: {
            padding: '8px 24px',
        },
        desktopMaxColumns: 4,
    },
};

export const footerStyles: FooterStyles = {
    footer: {
        position: 'relative',
        isolation: 'isolate',
        width: '100%',
    },
    topInner: {
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },
    columnsGrid: {
        display: 'grid',
        gap: '24px',
        alignItems: 'start',
    },
    column: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        margin: '0 0 10px 0',
        fontSize: '14px',
        lineHeight: 1.4,
        fontWeight: 700,
        letterSpacing: '0.01em',
    },
    link: {
        display: 'block',
        fontSize: '14px',
        lineHeight: 1.6,
        margin: '0 0 6px 0',
        textDecoration: 'none',
        transition: 'color 160ms ease',
        wordBreak: 'break-word',
    },
    brandBlock: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '12px',
    },
    logo: {
        display: 'block',
        maxWidth: '100%',
        height: '40px',
        width: 'auto',
        objectFit: 'contain',
    },
    socialWrap: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
    },
    bottomInner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
    },
    bottomText: {
        fontSize: '12px',
        lineHeight: 1.5,
        margin: 0,
    },
};
